import { useMutation } from '@tanstack/react-query';
import { Registry } from '@cosmjs/proto-signing';
import { defaultRegistryTypes } from '@cosmjs/stargate';
import {
  TxRaw,
  TxBody,
  AuthInfo,
  Fee,
} from 'cosmjs-types/cosmos/tx/v1beta1/tx.js';
import { PubKey as Secp256k1PubKey } from 'cosmjs-types/cosmos/crypto/secp256k1/keys.js';
import { Any } from 'cosmjs-types/google/protobuf/any.js';
import {
  buildSendTx,
  createGonkaSigner,
  broadcastAndConfirm,
  createRpcClient,
  GONKA_CHAIN,
  type BroadcastDeps,
  type BroadcastResult,
} from '@gonka-wallet/chain';
import {
  deriveKeyFromPin,
  decrypt,
  mnemonicBytesToSeed,
  deriveGonkaPrivateKey,
  derivePublicKey,
  zeroize,
} from '@gonka-wallet/crypto';
import type { EncryptedWalletBlob } from '@gonka-wallet/storage';
import { hexToBytes } from '../lib/format.js';
import { resolveSeeds } from '../lib/resolveSeeds.js';
import { useSettingsStore } from '../stores/settingsStore.js';

export interface SendInput {
  readonly wallet: EncryptedWalletBlob;
  readonly pin: string;
  readonly to: string;
  readonly amountNgonka: string;
  readonly memo?: string;
}

const registry = new Registry(defaultRegistryTypes);

/**
 * Sign-and-broadcast mutation. The decrypted key material exists only for
 * the duration of the mutation and is zeroized in `finally`. Seed derivation
 * uses `mnemonicBytesToSeed(plaintext)` (not `mnemonicToSeed(decode(...))`)
 * to minimize JS-string residency of the mnemonic (spec invariant I2).
 */
export function useSignAndBroadcast() {
  const chainNodeSource = useSettingsStore((s) => s.chainNodeSource);
  const customNodeUrl = useSettingsStore((s) => s.customNodeUrl);

  return useMutation({
    mutationFn: async (input: SendInput): Promise<string> => {
      // Initialize zeroization targets upfront so `finally` is never undefined.
      let kdfKey: Uint8Array = new Uint8Array(0);
      let plaintext: Uint8Array = new Uint8Array(0);
      let seed: Uint8Array = new Uint8Array(0);
      let priv: Uint8Array = new Uint8Array(0);

      try {
        // 1. Decrypt seed material.
        const salt = hexToBytes(input.wallet.salt);
        const nonce = hexToBytes(input.wallet.nonce);
        const ciphertext = hexToBytes(input.wallet.ciphertext);
        kdfKey = await deriveKeyFromPin(input.pin, salt);
        plaintext = decrypt(kdfKey, nonce, ciphertext);

        // 2. Derive BIP39 seed from mnemonic bytes (minimize JS-string residency).
        seed = await mnemonicBytesToSeed(plaintext);
        priv = deriveGonkaPrivateKey(seed);
        const pub = derivePublicKey(priv);

        // 3. Build unsigned tx.
        const fromAddress = input.wallet.addresses[0];
        if (!fromAddress) {
          throw new Error('wallet has no address');
        }
        const unsigned = buildSendTx({
          from: fromAddress,
          to: input.to,
          amountNgonka: input.amountNgonka,
          memo: input.memo,
        });

        // 4. Fetch account number + sequence via the failover REST client.
        // Honor the user's chain-node choice (Settings -> Chain node -> custom
        // node URL) so that signing + broadcast go through the same endpoint
        // as balance + history reads.
        const client = createRpcClient(resolveSeeds(chainNodeSource, customNodeUrl));
        const account = await client.getApi<{
          account: { account_number: string; sequence: string };
        }>(`/cosmos/auth/v1beta1/accounts/${fromAddress}`);
        const accountNumber = BigInt(account.account.account_number);
        const sequence = BigInt(account.account.sequence);

        // 5. Verify chain-id (safety check).
        const nodeInfo = await client.getApi<{
          default_node_info: { network: string };
        }>('/cosmos/base/tendermint/v1beta1/node_info');
        if (nodeInfo.default_node_info.network !== GONKA_CHAIN.chainId) {
          throw new Error(
            `chain-id mismatch: node says ${nodeInfo.default_node_info.network}, config says ${GONKA_CHAIN.chainId}`,
          );
        }

        // 6. Encode tx body and auth info.
        const bodyBytes = TxBody.encode(
          TxBody.fromPartial({
            messages: unsigned.messages.map((m) => registry.encodeAsAny(m)),
            memo: unsigned.memo,
          }),
        ).finish();

        // defaultRegistryTypes does NOT include /cosmos.crypto.secp256k1.PubKey
        // (it registers message types, not crypto types), so encode Any explicitly.
        const pubkeyAny = Any.fromPartial({
          typeUrl: '/cosmos.crypto.secp256k1.PubKey',
          value: Secp256k1PubKey.encode(
            Secp256k1PubKey.fromPartial({ key: pub }),
          ).finish(),
        });

        const authInfoBytes = AuthInfo.encode(
          AuthInfo.fromPartial({
            signerInfos: [
              {
                publicKey: pubkeyAny,
                modeInfo: { single: { mode: 1 /* SIGN_MODE_DIRECT */ } },
                sequence,
              },
            ],
            fee: Fee.fromPartial({
              amount: [],
              gasLimit: BigInt(unsigned.fee.gas),
            }),
          }),
        ).finish();

        // 7. Sign via OfflineDirectSigner.
        const signer = createGonkaSigner(priv);
        const signResponse = await signer.signDirect(fromAddress, {
          bodyBytes,
          authInfoBytes,
          chainId: GONKA_CHAIN.chainId,
          accountNumber,
        });
        const signatureBytes = base64ToBytes(signResponse.signature.signature);

        // 8. Build TxRaw and broadcast via failover client.
        const txRawBytes = TxRaw.encode(
          TxRaw.fromPartial({
            bodyBytes,
            authInfoBytes,
            signatures: [signatureBytes],
          }),
        ).finish();

        const deps: BroadcastDeps = {
          client,
          broadcastTx: async (bytes): Promise<BroadcastResult> => {
            const resp = await client.postApi<{
              tx_response: { code: number; txhash: string; raw_log: string };
            }>('/cosmos/tx/v1beta1/txs', {
              tx_bytes: bytesToBase64(bytes),
              mode: 'BROADCAST_MODE_SYNC',
            });
            return {
              code: resp.tx_response.code,
              transactionHash: resp.tx_response.txhash,
              rawLog: resp.tx_response.raw_log ?? '',
            };
          },
        };

        return await broadcastAndConfirm(deps, txRawBytes);
      } finally {
        zeroize(kdfKey, plaintext, seed, priv);
      }
    },
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
